import { Button } from '@daily/shared/components/Button';
import { Modal } from '@daily/shared/components/Modal';
import { Text } from '@daily/shared/components/Text';
import { DailyEventObjectMeetingSessionStateUpdated } from '@daily-co/daily-js';
import { useDaily } from '@daily-co/daily-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import deepEqual from 'fast-deep-equal';
import React, {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  BreakoutsSessionData,
  useBreakouts,
} from '../../contexts/BreakoutsProvider';
import { ContentContainer } from './ContentContainer';
import { CtaButtonContainer } from './CtaButtonContainer';
import { MAIN_ROOM_IDENTIFIER } from './helpers/constants';
import { useBreakoutSessionState } from './hooks/useBreakoutSessionState';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useRoomMapping } from './hooks/useRoomMapping';
import { useRooms } from './hooks/useRooms';
import { Room } from './Room';

const GAP = 16;

type OngoingFormState = 'idle' | 'dirty' | 'submitting';

export const OngoingBreakoutSession: React.FC<React.PropsWithChildren> = memo(
  () => {
    const { t } = useTranslation();
    const daily = useDaily();
    const { endBreakouts } = useBreakouts();
    const [showEndModal, setShowEndModal] = useState(false);

    const { addRoom, deleteRoom, rooms } = useRooms();
    const {
      currentRoomMapping,
      getRoomMappingByRoomId,
      publishRoomMapping,
      syncRoomMappingFromSessionState,
    } = useRoomMapping();
    const { dragParticipant, dropParticipant } = useDragAndDrop();
    const [formState, setFormState] = useState<OngoingFormState>('idle');

    const { breakoutStatus, endDate } = useBreakoutSessionState({
      onMeetingSessionStateUpdated: useCallback(
        (ev) => {
          if (formState === 'submitting') return;
          syncRoomMappingFromSessionState(ev.meetingSessionState.data);
        },
        [formState, syncRoomMappingFromSessionState]
      ),
    });

    const handleDropParticipant = (ev) => {
      dropParticipant(ev);
      const participantId = ev.dataTransfer.getData('text/plain');
      const roomIdentifier = ev.currentTarget.dataset.roomIdentifier;
      const mapping = getRoomMappingByRoomId(roomIdentifier);

      if (!mapping.includes(participantId)) {
        setFormState('dirty');
      }
    };

    const handleEndClick = () => {
      setShowEndModal(true);
    };

    const handleEndTimerClick = () => {
      setShowEndModal(false);
      endBreakouts();
    };

    const handleEndNowClick = () => {
      setShowEndModal(false);
      endBreakouts(true);
    };

    const scrollRef = useRef<HTMLDivElement>(null);
    const virtualizer = useVirtualizer({
      count: rooms.length + 1,
      estimateSize: (index) => {
        switch (index) {
          case 0:
            return 142 + GAP;
          default:
            return 122 + GAP;
        }
      },
      getScrollElement: () => scrollRef.current,
      overscan: 2,
    });

    const virtualItems = virtualizer.getVirtualItems();

    const isStateInSync = useCallback(
      (
        data: BreakoutsSessionData = daily?.meetingSessionState()
          .data as BreakoutsSessionData
      ) => {
        const idToRoomMapping = {};
        Object.entries(data?.br?.mapping ?? {}).forEach(
          ([roomId, { users }]) => {
            users.forEach((id) => {
              idToRoomMapping[id] = roomId;
            });
          }
        );
        /**
         * When participants are moved back to main, the currentRoomMapping contains
         * entries to MAIN_ROOM_IDENTIFIER, but these are not reflected in the
         * meeting session state.
         * For correct comparison we'll have to remove main room mapping entries
         * from the current (= local state) mapping object.
         */
        const cleanCurrentMapping = {};
        Object.entries(currentRoomMapping).forEach(([id, roomId]) => {
          if (roomId !== MAIN_ROOM_IDENTIFIER) {
            cleanCurrentMapping[id] = roomId;
          }
        });
        return deepEqual(idToRoomMapping, cleanCurrentMapping);
      },
      [currentRoomMapping, daily]
    );

    const handleClickApplyChanges = () => {
      if (!daily) return;

      if (isStateInSync()) {
        setFormState('idle');
        return;
      }

      /**
       * Wait for new mapping to be published, before re-enabling automatic sync.
       */
      const handleStateUpdate = (
        ev: DailyEventObjectMeetingSessionStateUpdated
      ) => {
        const data = ev.meetingSessionState.data as BreakoutsSessionData;
        if (isStateInSync(data)) {
          setFormState('idle');
          syncRoomMappingFromSessionState(data);
          daily.off('meeting-session-state-updated', handleStateUpdate);
        }
      };
      setFormState('submitting');
      daily.on('meeting-session-state-updated', handleStateUpdate);
      publishRoomMapping();
    };

    const handleClickDiscardChanges = () => {
      setFormState('idle');
      syncRoomMappingFromSessionState(
        daily.meetingSessionState().data as BreakoutsSessionData
      );
    };

    const handleChangeInDropdown = () => {
      setFormState('dirty');
    };

    /**
     * Re-syncs mapping from meeting session state when the breakout panel is opened.
     */
    useEffect(
      function initMappingState() {
        if (!daily) return;
        const data = daily.meetingSessionState().data as BreakoutsSessionData;
        syncRoomMappingFromSessionState(data);
        const mapping = data?.br?.mapping ?? {};
        if (rooms.some((id) => !(id in mapping))) {
          rooms.forEach(deleteRoom);
          Object.keys(mapping).forEach(addRoom);
        }
      },
      [addRoom, daily, deleteRoom, rooms, syncRoomMappingFromSessionState]
    );

    return (
      <>
        <ContentContainer
          ref={scrollRef}
          innerHeight={virtualizer.getTotalSize()}
          data-testid="ongoing"
        >
          {virtualItems.map((item) => {
            const style: CSSProperties = {
              transform: `translateY(${item.start}px)`,
            };
            switch (item.index) {
              case 0:
                return (
                  <div
                    key="main-room"
                    id="main-room-config"
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                    className="row"
                    style={style}
                  >
                    <Room
                      onDragOver={dragParticipant}
                      onDrop={handleDropParticipant}
                      onResize={() =>
                        virtualizer.measureElement(
                          document.getElementById('main-room-config')
                        )
                      }
                      roomIdentifier={MAIN_ROOM_IDENTIFIER}
                      onChangeSelect={handleChangeInDropdown}
                    />
                  </div>
                );
              default:
                const index = item.index - 1;
                const roomId = rooms[index];
                const domId = `room-config-${roomId}`;
                return (
                  <div
                    key={roomId}
                    id={domId}
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                    className="row"
                    style={style}
                  >
                    <Room
                      onDragOver={dragParticipant}
                      onDrop={handleDropParticipant}
                      onResize={() =>
                        virtualizer.measureElement(
                          document.getElementById(domId)
                        )
                      }
                      roomIdentifier={roomId}
                      roomName={index + 1}
                      onChangeSelect={handleChangeInDropdown}
                    />
                  </div>
                );
            }
          })}
        </ContentContainer>
        <CtaButtonContainer>
          {breakoutStatus === 'ongoing' && formState !== 'idle' && (
            <>
              <Button
                id="breakouts-apply-changes"
                fullWidth
                loading={formState === 'submitting'}
                onClick={handleClickApplyChanges}
                variant="primary"
              >
                {t('breakouts.applyChanges')}
              </Button>
              <Button
                id="breakouts-discard-changes"
                disabled={formState === 'submitting'}
                fullWidth
                onClick={handleClickDiscardChanges}
                variant="secondary"
              >
                {t('breakouts.discardChanges')}
              </Button>
            </>
          )}
          <Button
            id="breakouts-end-session"
            fullWidth
            onClick={handleEndClick}
            variant="danger"
          >
            {t('breakouts.endBreakoutSessionEllipsis')}
          </Button>
        </CtaButtonContainer>
        {showEndModal && (
          <Modal
            onClose={() => setShowEndModal(false)}
            actions={[
              <Button
                id="breakouts-end-in-60-seconds"
                key="endTimer"
                disabled={!!endDate}
                variant="primary"
                onClick={handleEndTimerClick}
              >
                {t('breakouts.endBreakoutModal.endIn60Seconds')}
              </Button>,
              <Button
                id="breakouts-end-now"
                key="endNow"
                variant="danger"
                onClick={handleEndNowClick}
              >
                {t('breakouts.endBreakoutModal.endImmediately')}
              </Button>,
              <Button
                id="breakouts-end-cancel"
                key="cancel"
                variant="secondary"
              >
                {t('general.cancel')}
              </Button>,
            ]}
            title={t('breakouts.endBreakoutSession')}
          >
            <Text El="p" variant="large">
              {t('breakouts.endBreakoutModal.desc')}
            </Text>
          </Modal>
        )}
        <style jsx>{`
          .row {
            left: 0;
            padding-bottom: ${GAP}px;
            position: absolute;
            top: 0;
            width: 100%;
          }
        `}</style>
      </>
    );
  }
);
OngoingBreakoutSession.displayName = 'OngoingBreakoutSession';
